/**
 * UsageMeter — Compact daily usage indicator
 *
 * Shows remaining AI and Search queries for the day as small progress bars.
 * Fetches from GET /api/usageSummary and refreshes after each AI/search call.
 */
import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders } from '../../services/apiAuth';
import { getApiBaseUrl } from '../../utils/apiConfig';
import TokenPurchaseModal from './TokenPurchaseModal';

interface TierUsage {
  used: number;
  budget: number;
  remaining: number;
}

interface UsageSummary {
  ai: TierUsage;
  search: TierUsage;
  bonusQueries: number;
  purchasedQueries: number;
  engagementCount: number;
  isAdmin?: boolean;
}

export interface UsageMeterHandle {
  /** Call after an AI or search request to refresh usage */
  refresh: () => void;
  /** True when no AI queries remain for today */
  isAiExhausted: () => boolean;
  /** True when no search queries remain for today */
  isSearchExhausted: () => boolean;
  /** Open the token purchase modal */
  openPurchase: () => void;
}

interface UsageMeterProps {
  language: string;
  /** Callback when daily budget state changes (optional) */
  onBudgetChange?: (aiRemaining: number, searchRemaining: number) => void;
}

const UsageMeter = forwardRef<UsageMeterHandle, UsageMeterProps>(
  function UsageMeter({ language, onBudgetChange }, ref) {
    const { t } = useTranslation('chat');
    const [usage, setUsage] = useState<UsageSummary | null>(null);
    const [collapsed, setCollapsed] = useState(true);
    const [showPurchase, setShowPurchase] = useState(false);

    const fetchUsage = useCallback(async () => {
      try {
        const headers = await getAuthHeaders();
        const baseUrl = getApiBaseUrl();
        const res = await fetch(`${baseUrl}/usageSummary`, { headers });
        if (res.ok) {
          const data: UsageSummary = await res.json();
          setUsage(data);
          onBudgetChange?.(data.ai.remaining, data.search.remaining);
        } else {
          console.warn(`[UsageMeter] /api/usageSummary returned ${res.status}`);
        }
      } catch (err) {
        console.warn('[UsageMeter] Failed to fetch usage:', err);
      }
    }, [onBudgetChange]);

    // Expose refresh + exhaustion checks via ref
    useImperativeHandle(ref, () => ({
      refresh: fetchUsage,
      isAiExhausted: () => usage?.ai.remaining === 0,
      isSearchExhausted: () => usage?.search.remaining === 0,
      openPurchase: () => setShowPurchase(true),
    }), [fetchUsage, usage]);

    // Fetch on mount
    useEffect(() => { fetchUsage(); }, [fetchUsage]);

    if (!usage) return null;

    // Admin users have unlimited quota — show compact indicator
    if (usage.isAdmin) {
      return (
        <div className="usage-meter" title={t('usage.adminUnlimited')}>
          <div className="usage-meter-summary">
            <span className="usage-meter-label">🤖 AI ∞</span>
            <span className="usage-meter-label">🔍 {t('usage.search')} ∞</span>
            <span className="usage-meter-bonus" style={{ opacity: 0.6 }}>👑 Admin</span>
          </div>
        </div>
      );
    }

    const pct = (t: TierUsage) =>
      t.budget === 0 ? 100 : Math.round((t.used / t.budget) * 100);

    const barColor = (t: TierUsage) => {
      const p = pct(t);
      if (p >= 90) return '#ef4444'; // red
      if (p >= 70) return '#f59e0b'; // amber
      return '#10b981'; // green
    };

    const isLow = usage.ai.remaining <= 3 || usage.search.remaining <= 3;
    const aiExhausted = usage.ai.remaining === 0;
    const searchExhausted = usage.search.remaining === 0;
    const isExhausted = aiExhausted || searchExhausted;

    const exhaustedMessage = () => {
      if (aiExhausted && searchExhausted) {
        return t('usage.dailyLimitReached');
      }
      if (searchExhausted) {
        return t('usage.searchLimitReached');
      }
      return t('usage.aiLimitReached');
    };

    return (
      <div
        className={`usage-meter${isExhausted ? ' usage-meter--exhausted' : isLow ? ' usage-meter--low' : ''}`}
        onClick={() => setCollapsed(c => !c)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCollapsed(c => !c); } }}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        title={t('usage.dailyUsageTitle')}
      >
        {/* Low/exhausted warning */}
        {isExhausted && (
          <div className="usage-meter-warning">
            {exhaustedMessage()}
            {(aiExhausted || searchExhausted) && (
              <button
                className="usage-meter-warning-buy"
                onClick={(e) => { e.stopPropagation(); setShowPurchase(true); }}
                type="button"
              >
                {t('usage.buyMore')}
              </button>
            )}
          </div>
        )}
        {isLow && !isExhausted && (
          <div className="usage-meter-warning usage-meter-warning--low">
            {t('usage.runningLow')}
          </div>
        )}

        {/* Collapsed: single line summary */}
        <div className="usage-meter-summary">
          <span className="usage-meter-label" title={t('usage.aiTooltip', { used: usage.ai.used, budget: usage.ai.budget, remaining: usage.ai.remaining, defaultValue: 'Daily AI consultations: {{used}} used of {{budget}} ({{remaining}} remaining)' })}>
            🤖 <span className="usage-meter-tier-name">AI</span> {usage.ai.used}/{usage.ai.budget}
          </span>
          <div className="usage-meter-minibar" title={t('usage.aiTooltip', { used: usage.ai.used, budget: usage.ai.budget, remaining: usage.ai.remaining, defaultValue: 'Daily AI consultations: {{used}} used of {{budget}} ({{remaining}} remaining)' })}>
            <div
              className="usage-meter-minibar-fill"
              style={{ width: `${pct(usage.ai)}%`, backgroundColor: barColor(usage.ai) }}
            />
          </div>
          <span className="usage-meter-label" title={t('usage.searchTooltip', { used: usage.search.used, budget: usage.search.budget, remaining: usage.search.remaining, defaultValue: 'Daily product searches: {{used}} used of {{budget}} ({{remaining}} remaining)' })}>
            🔍 <span className="usage-meter-tier-name">{t('usage.search')}</span> {usage.search.used}/{usage.search.budget}
          </span>
          <div className="usage-meter-minibar" title={t('usage.searchTooltip', { used: usage.search.used, budget: usage.search.budget, remaining: usage.search.remaining, defaultValue: 'Daily product searches: {{used}} used of {{budget}} ({{remaining}} remaining)' })}>
            <div
              className="usage-meter-minibar-fill"
              style={{ width: `${pct(usage.search)}%`, backgroundColor: barColor(usage.search) }}
            />
          </div>
          {usage.bonusQueries > 0 && (
            <span className="usage-meter-bonus">
              +{usage.bonusQueries} {t('usage.monthlyBonus')}
            </span>
          )}
          <span className={`usage-meter-expand-hint${collapsed ? '' : ' expanded'}`} aria-hidden="true">▾</span>
        </div>

        {/* Expanded: detailed breakdown + earn-more guide */}
        {!collapsed && (
          <div className="usage-meter-details">
            <div className="usage-meter-row">
              <span>{t('usage.aiConsultations')}</span>
              <span>{usage.ai.used} / {usage.ai.budget}</span>
            </div>
            <div className="usage-meter-bar">
              <div
                className="usage-meter-bar-fill"
                style={{ width: `${pct(usage.ai)}%`, backgroundColor: barColor(usage.ai) }}
              />
            </div>
            <div className="usage-meter-row">
              <span>{t('usage.searchQueries')}</span>
              <span>{usage.search.used} / {usage.search.budget}</span>
            </div>
            <div className="usage-meter-bar">
              <div
                className="usage-meter-bar-fill"
                style={{ width: `${pct(usage.search)}%`, backgroundColor: barColor(usage.search) }}
              />
            </div>
            {usage.bonusQueries > 0 && (
              <div className="usage-meter-row" style={{ fontSize: '11px', opacity: 0.8 }}>
                <span>{t('usage.engagementBonus')}</span>
                <span>+{usage.bonusQueries}</span>
              </div>
            )}

            {/* Earn-more guide */}
            <div className="usage-meter-earnmore">
              <div className="usage-meter-earnmore-title">
                {t('usage.bonusQuests')}
              </div>
              <div className="usage-meter-earnmore-item">
                🛒 {t('usage.questPurchase')}
              </div>
              <div className="usage-meter-earnmore-item">
                🔗 {t('usage.questClick')}
              </div>
              <div className="usage-meter-earnmore-item">
                👀 {t('usage.questView')}
              </div>
              <div className="usage-meter-earnmore-item">
                💬 {t('usage.questFeedback')}
              </div>
              <div className="usage-meter-earnmore-note">
                {t('usage.bonusActivities', { count: usage.engagementCount })}
              </div>
            </div>

            {/* Buy More Queries — when AI or search is exhausted */}
            {(aiExhausted || searchExhausted) && (
              <button
                className="usage-meter-buy-btn"
                onClick={(e) => { e.stopPropagation(); setShowPurchase(true); }}
                type="button"
              >
                💳 {t('usage.buyMoreQueries')}
              </button>
            )}
          </div>
        )}

        {/* Token Purchase Modal */}
        {showPurchase && (
          <TokenPurchaseModal
            language={language}
            onClose={() => setShowPurchase(false)}
            onSuccess={() => { setShowPurchase(false); fetchUsage(); }}
          />
        )}
      </div>
    );
  }
);

export default UsageMeter;
